import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { KeyRoundIcon, Trash2Icon } from "lucide-react";
import { useState, type SubmitEvent } from "react";
import { Confirm } from "@/components/confirm";
import { ErrorText, PageHeader } from "@/components/page";
import { RecoveryKeyCard } from "@/components/recovery-key-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/icon-button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errorMessage } from "@/lib/api";
import { fmtDateTime, timeAgo } from "@/lib/format";
import { useDevices, useUnlocked } from "@/lib/hooks";
import { changePassword, rotateRecoveryKey } from "@/lib/session";
import { registerPasskey } from "@/lib/webauthn";

export const Route = createFileRoute("/_app/security")({ component: SecurityPage });

function SecurityPage() {
  return (
    <>
      <PageHeader
        title="Security"
        description="Ways into your vault. Each one wraps the same vault key; the server can open none of them."
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <PasskeysCard />
        <PasswordCard />
        <RecoveryCard />
        <SessionsCard />
      </div>
    </>
  );
}

function PasskeysCard() {
  const s = useUnlocked();
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["passkeys"], queryFn: () => api.passkeys.list(), select: (d) => d.passkeys });
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const supported = typeof window !== "undefined" && "PublicKeyCredential" in window;
  const add = async () => {
    setBusy(true);
    setError(null);
    try {
      await registerPasskey(name.trim() || "Passkey", s.vk);
      await qc.invalidateQueries({ queryKey: ["passkeys"] });
      setAdding(false);
      setName("");
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const del = useMutation({
    mutationFn: (credential_id: string) => api.passkeys.delete({ credential_id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["passkeys"] }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Passkeys</CardTitle>
        <CardDescription>
          Sign in with Touch ID or a security key. Authenticators that support PRF also unlock the vault without a
          password.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {list.data?.length === 0 ? <p className="text-sm text-muted-foreground">No passkeys yet.</p> : null}
        {list.data?.map((p) => (
          <div key={p.credential_id} className="flex items-center gap-3 text-sm" data-testid="passkey-row">
            <KeyRoundIcon className="size-4 text-muted-foreground" />
            <div className="min-w-0 flex-1">
              <p className="font-medium">
                {p.name}{" "}
                {p.prf_enabled ? (
                  <Badge variant="secondary">unlocks vault</Badge>
                ) : (
                  <Badge variant="outline">sign-in only</Badge>
                )}
              </p>
              <p className="text-xs text-muted-foreground">
                added {fmtDateTime(p.created_at)} · used {timeAgo(p.last_used_at)}
              </p>
            </div>
            <Confirm
              trigger={
                <IconButton label={`Delete passkey ${p.name}`}>
                  <Trash2Icon />
                </IconButton>
              }
              title={`Delete passkey ${p.name}?`}
              description="You can no longer sign in with it."
              actionLabel="Delete"
              destructive
              onConfirm={() => del.mutateAsync(p.credential_id).then(() => undefined)}
            />
          </div>
        ))}
        <ErrorText error={del.error} />
        <Button
          variant="outline"
          size="sm"
          onClick={() => setAdding(true)}
          disabled={!supported}
          data-testid="add-passkey"
        >
          Add passkey
        </Button>
        {!supported ? <p className="text-xs text-muted-foreground">This browser does not support passkeys.</p> : null}
      </CardContent>
      <Dialog open={adding} onOpenChange={(o) => !o && setAdding(false)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a passkey</DialogTitle>
            <DialogDescription>Give it a name you will recognize, then follow your browser's prompt.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="pk-name">Name</Label>
            <Input id="pk-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="MacBook Touch ID" />
          </div>
          <ErrorText error={error} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button onClick={() => void add()} disabled={busy} data-testid="create-passkey">
              {busy ? "Waiting for authenticator…" : "Create passkey"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function PasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  const submit = async (e: SubmitEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setOk(false);
    if (next !== confirm) {
      setError("New passwords do not match");
      return;
    }
    if (next.length < 10) {
      setError("Use at least 10 characters");
      return;
    }
    setBusy(true);
    try {
      await changePassword(current, next);
      setOk(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Password</CardTitle>
        <CardDescription>Changing it re-wraps the vault key; other devices keep working.</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="space-y-3" onSubmit={(e) => void submit(e)}>
          <div className="space-y-1.5">
            <Label htmlFor="cur">Current password</Label>
            <Input
              id="cur"
              type="password"
              autoComplete="current-password"
              required
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new">New password</Label>
            <Input
              id="new"
              type="password"
              autoComplete="new-password"
              required
              value={next}
              onChange={(e) => setNext(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="conf">Confirm new password</Label>
            <Input
              id="conf"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <ErrorText error={error} />
          {ok ? <p className="text-sm text-success">Password changed.</p> : null}
          <Button type="submit" disabled={busy}>
            {busy ? "Working…" : "Change password"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function RecoveryCard() {
  const [key, setKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rotate = async () => {
    setBusy(true);
    setError(null);
    try {
      setKey(await rotateRecoveryKey());
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Recovery key</CardTitle>
        <CardDescription>
          Resets your password if you forget it. Rotating invalidates the previous key immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {key ? (
          <RecoveryKeyCard recoveryKey={key} onDone={() => setKey(null)} />
        ) : (
          <Confirm
            trigger={
              <Button variant="outline" size="sm" disabled={busy}>
                Rotate recovery key
              </Button>
            }
            title="Rotate the recovery key?"
            description="The old key stops working the moment the new one is created. You will need to store the new one."
            actionLabel="Rotate"
            onConfirm={rotate}
          />
        )}
        <ErrorText error={error} />
      </CardContent>
    </Card>
  );
}

function SessionsCard() {
  const qc = useQueryClient();
  const sessions = useQuery({ queryKey: ["sessions"], queryFn: () => api.sessions.list(), select: (d) => d.sessions });
  const devices = useDevices();
  const names = new Map((devices.data ?? []).map((d) => [d.id, d.name]));
  const revoke = useMutation({
    mutationFn: (session_id: string) => api.sessions.revoke({ session_id }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["sessions"] }),
  });
  return (
    <Card>
      <CardHeader>
        <CardTitle>Active sessions</CardTitle>
        <CardDescription>Browser cookies and CLI device tokens that can currently talk to the API.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {sessions.data?.map((s) => (
          <div key={s.id} className="flex items-center gap-3 text-sm">
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium">
                {s.kind === "device" ? "CLI" : "Browser"}
                {s.device_id ? ` · ${names.get(s.device_id) ?? s.device_id.slice(0, 8)}` : ""}{" "}
                {s.current ? <Badge variant="secondary">current</Badge> : null}
              </p>
              <p className="truncate text-xs text-muted-foreground" title={s.user_agent ?? ""}>
                started {fmtDateTime(s.created_at)} · active {timeAgo(s.last_used_at)}
              </p>
            </div>
            {!s.current ? (
              <Button variant="ghost" size="sm" onClick={() => revoke.mutate(s.id)} disabled={revoke.isPending}>
                Revoke
              </Button>
            ) : null}
          </div>
        ))}
        <ErrorText error={revoke.error} />
      </CardContent>
    </Card>
  );
}
